import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useSupabaseClient } from "./supabase";
import { getQuizToken, quizIdForRequest } from "../lib/quizTokens";

export const useHtClient = () => {
  const { data: supabase } = useSupabaseClient();

  return useQuery({
    queryKey: ["useHtClient"],
    enabled: !!supabase,
    queryFn: async () => {
      console.log("🔄 HTTP CLIENT: Initializing...");

      const apiUrl = process.env.REACT_APP_API_URL;

      if (!apiUrl) {
        console.log("❌ HTTP CLIENT: Missing API URL configuration");
        throw new Error(
          "API URL configuration missing. Please check your .env file."
        );
      }

      if (!supabase) {
        console.log("❌ HTTP CLIENT: Missing Supabase client");
        throw new Error("Supabase client not initialized");
      }

      const client = axios.create({
        baseURL: apiUrl,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      client.interceptors.request.use(async (config) => {
        console.log("🔄 HTTP CLIENT: Intercepting request...");
        console.log("🔗 HTTP CLIENT: Request URL:", config.url);

        // Attach the login token whenever a session exists, on EVERY request. The
        // backend decides what is public (ht-backend app/utils/access_policy.py, default
        // deny). The old client-side "public endpoint" substring list stripped the token
        // from routes like /leads/detailed and /quizzes that now require login (2026-09-24).
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("❌ HTTP CLIENT: Error getting session:", error);
        }

        console.log("🔑 HTTP CLIENT: Session state:", {
          hasSession: !!session,
          accessToken: session?.access_token ? "present" : "missing",
          refreshToken: session?.refresh_token ? "present" : "missing",
          expiresIn: session?.expires_in,
          expiresAt: session?.expires_at,
        });

        if (session?.access_token && session?.refresh_token) {
          config.headers["Authorization"] = `Bearer ${session.access_token}`;
          config.headers["X-Refresh-Token"] = session.refresh_token;
          console.log("✅ HTTP CLIENT: Set both tokens in headers");
        }

        // Anonymous quiz writes must prove this browser created the quiz (lib/quizTokens.ts).
        const quizToken = getQuizToken(quizIdForRequest(config.url, config.method, config.data));
        if (quizToken) {
          config.headers["X-Quiz-Token"] = quizToken;
        }

        return config;
      });

      // Add response interceptor to handle JWT expiration
      client.interceptors.response.use(
        (response) => {
          // Return successful responses as-is
          return response;
        },
        async (error) => {
          const originalRequest = error.config;

          // No client-side public list: a 401 on any route may be an expired token.
          // Anonymous visitors have no session, so refreshSession() fails and we reject.

          // Check if error is due to JWT expiration or unauthorized access
          const isAuthError = error.response?.status === 401 || 
                            error.response?.status === 403 ||
                            (error.response?.status === 400 && 
                             error.response?.data?.detail?.toLowerCase()?.includes('jwt'));
          
          const isExpiredToken = error.response?.data?.detail?.toLowerCase()?.includes('expired') ||
                               error.response?.data?.detail?.toLowerCase()?.includes('invalid token') ||
                               error.response?.data?.detail?.toLowerCase()?.includes('token');
          
          if (isAuthError && isExpiredToken && !originalRequest._retry) {
            console.log(
              "🔄 HTTP CLIENT: JWT expired, attempting to refresh session..."
            );
            originalRequest._retry = true;

            try {
              // Refresh the session
              const {
                data: { session },
                error: refreshError,
              } = await supabase.auth.refreshSession();

              if (refreshError) {
                console.error(
                  "❌ HTTP CLIENT: Failed to refresh session:",
                  refreshError
                );
                // Redirect to login or handle auth failure
                throw refreshError;
              }

              if (session?.access_token) {
                console.log("✅ HTTP CLIENT: Session refreshed successfully");
                // Update the original request with new token
                originalRequest.headers[
                  "Authorization"
                ] = `Bearer ${session.access_token}`;
                if (session.refresh_token) {
                  originalRequest.headers["X-Refresh-Token"] =
                    session.refresh_token;
                }

                // Retry the original request
                return client(originalRequest);
              }
            } catch (refreshError) {
              console.error(
                "❌ HTTP CLIENT: Token refresh failed:",
                refreshError
              );
              // Could redirect to login here
              return Promise.reject(refreshError);
            }
          }

          return Promise.reject(error);
        }
      );

      console.log("✅ HTTP CLIENT: Initialized with base URL:", apiUrl);
      return client;
    },
    staleTime: Infinity,
  });
};
